import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useBoardPrefetch } from '../hooks/useBoardPrefetch';
import ThemeToggle from '../components/ThemeToggle';
import TrialBanner from '../components/TrialBanner';
import ReadOnlyBanner from '../components/ReadOnlyBanner';
import OptimizedImage from '../components/OptimizedImage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Briefcase, LayoutGrid, ListTodo, Clock, TrendingUp, Users, LogOut, ChevronDown, Star, Home, Loader2, X, MessageSquare, MessageCircle, Github, Zap, BarChart3, Download, Shield, Settings as SettingsIcon, Search, Bell, Webhook, AlertTriangle, CalendarDays, DollarSign } from 'lucide-react';
import BoardIcon, { BOARD_ICONS } from '../components/BoardIcon';
import WebhooksSettings from '../components/WebhooksSettings';
import TodayFeed from '../components/TodayFeed';
import GitHubIntegration from '../components/GitHubIntegration';
import BoardTypeSelector from '../components/BoardTypeSelector';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { API_BASE_URL } from '../config';

const BACKEND_URL = API_BASE_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { fetchSubscriptionStatus, canEdit, isTrialActive, isExpired } = useSubscription();
  const { prefetchBoard } = useBoardPrefetch();
  
  // Get workspace from URL params if present
  const searchParams = new URLSearchParams(window.location.search);
  const urlWorkspaceId = searchParams.get('workspace');
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    workspaces: 0,
    boards: 0,
    totalTasks: 0,
    tasksByStatus: {}
  });
  const [recentTasks, setRecentTasks] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [boards, setBoards] = useState([]);
  
  // Modals
  const [showMembersPanel, setShowMembersPanel] = useState(false);
  const [showWorkspaceWebhooksPanel, setShowWorkspaceWebhooksPanel] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCreateWorkspace, setShowCreateWorkspace] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  
  // Board creation
  const [showCreateBoard, setShowCreateBoard] = useState(false);
  const [showBoardTypeSelector, setShowBoardTypeSelector] = useState(false);
  const [selectedBoardType, setSelectedBoardType] = useState('project');
  const [newBoardName, setNewBoardName] = useState('');
  const [newBoardIcon, setNewBoardIcon] = useState('briefcase');
  const [newBoardColor, setNewBoardColor] = useState('#FE8309');
  
  // Invitations
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [sentInvitations, setSentInvitations] = useState([]);
  const [loadingSentInvitations, setLoadingSentInvitations] = useState(false);
  
  // Workspace Management Dialogs
  const [showDeleteWorkspaceDialog, setShowDeleteWorkspaceDialog] = useState(false);
  const [showTransferOwnershipDialog, setShowTransferOwnershipDialog] = useState(false);
  const [selectedNewOwner, setSelectedNewOwner] = useState('');
  
  // GitHub Integration
  const [showGitHubPanel, setShowGitHubPanel] = useState(false);
  const [showInvitations, setShowInvitations] = useState(false);
  const [todayData, setTodayData] = useState({ kpis: { overdue_count: 0, today_count: 0, pipeline_value: 0 }, overdue: [], today: [], this_week: [] });
  const [todayLoading, setTodayLoading] = useState(false);



  // Helper to get user's role in workspace
  const getUserRole = (workspace) => {
    if (!workspace || !user) return null;
    if (workspace.owner_id === user._id) return 'owner';
    const member = workspace.members?.find(m => m.user_id === user._id);
    return member?.role || null;
  };

  const fetchWorkspaceMembers = async () => {
    if (!currentWorkspace) return;
    try {
      const response = await axios.get(`${API}/workspaces/${currentWorkspace._id}/members`, { withCredentials: true });
      setWorkspaceMembers(response.data);
      
      // Also fetch sent invitations if user is owner/admin
      if (userRole === 'owner' || userRole === 'admin') {
        fetchSentInvitations();
      }
    } catch (error) {
      console.error('Error fetching workspace members:', error);
    }
  };


  const fetchPendingInvitations = async () => {
    try {
      setLoadingInvitations(true);
      const response = await axios.get(`${API}/invitations/pending`, { withCredentials: true });
      setPendingInvitations(response.data);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    } finally {
      setLoadingInvitations(false);
    }
  };

  const handleAcceptInvitation = async (invitationId) => {
    try {
      await axios.post(`${API}/invitations/${invitationId}/accept`, {}, { withCredentials: true });
      toast({ title: '✅ Invitation accepted!', description: 'You now have access to the workspace' });
      setPendingInvitations(pendingInvitations.filter(i => i._id !== invitationId));
      
      // Refresh workspaces to show the new one
      const wsResponse = await axios.get(`${API}/workspaces`, { withCredentials: true });
      setWorkspaces(wsResponse.data);
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast({ 
        title: 'Error accepting invitation', 
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive' 
      });
    }
  };

  const handleDeclineInvitation = async (invitationId) => {
    try {
      await axios.post(`${API}/invitations/${invitationId}/reject`, {}, { withCredentials: true });
      toast({ title: 'Invitation declined' });
      setPendingInvitations(pendingInvitations.filter(i => i._id !== invitationId));
    } catch (error) {
      console.error('Error declining invitation:', error);
      toast({ title: 'Error declining invitation', variant: 'destructive' });

  const fetchSentInvitations = async () => {
    if (!currentWorkspace) return;
    
    try {
      setLoadingSentInvitations(true);
      const response = await axios.get(
        `${API}/workspaces/${currentWorkspace._id}/invitations/sent`,
        { withCredentials: true }
      );
      setSentInvitations(response.data);
    } catch (error) {
      console.error('Error fetching sent invitations:', error);
    } finally {
      setLoadingSentInvitations(false);
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      await axios.post(
        `${API}/workspaces/${currentWorkspace._id}/invite/${invitationId}/resend`,
        {},
        { withCredentials: true }
      );
      toast({ title: 'Invitation resent successfully!' });
      fetchSentInvitations();
    } catch (error) {


  const handleDeleteInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to delete this invitation?')) return;
    
    try {
      await axios.delete(
        `${API}/workspaces/${currentWorkspace._id}/invitations/${invitationId}`,
        { withCredentials: true }
      );
      toast({ title: 'Invitation deleted' });
      fetchSentInvitations();
    } catch (error) {
      console.error('Error deleting invitation:', error);
      toast({ title: 'Error deleting invitation', variant: 'destructive' });
    }
  };

      console.error('Error resending invitation:', error);
      toast({ 
        title: 'Error resending invitation',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive' 
      });
    }
  };

    }
  };

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim()) {
      toast({ title: 'Please enter a workspace name', variant: 'destructive' });
      return;
    }

    try {
      const response = await axios.post(`${API}/workspaces`, { name: newWorkspaceName }, { withCredentials: true });
      const newWorkspace = response.data;
      setWorkspaces([...workspaces, newWorkspace]);
      setCurrentWorkspace(newWorkspace);
      setUserRole(getUserRole(newWorkspace));
      setNewWorkspaceName('');
      setShowCreateWorkspace(false);
      fetchDashboardData();
      toast({ title: 'Workspace created successfully!' });
    } catch (error) {
      console.error('Error creating workspace:', error);
      toast({ title: 'Error creating workspace', variant: 'destructive' });
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) {
      toast({ title: 'Please enter an email address', variant: 'destructive' });
      return;
    }

    try {
      await axios.post(
        `${API}/workspaces/${currentWorkspace._id}/invite`,
        { email: inviteEmail, role: inviteRole },
        { withCredentials: true }
      );
      setInviteEmail('');
      setInviteRole('member');
      setShowInviteModal(false);
      fetchWorkspaceMembers();
      toast({ title: `Invitation sent to ${inviteEmail}!` });
    } catch (error) {
      console.error('Error inviting member:', error);
      toast({ title: error.response?.data?.detail || 'Error inviting member', variant: 'destructive' });
    }
  };

  const handleChangeMemberRole = async (memberId, newRole) => {
    try {
      await axios.put(
        `${API}/workspaces/${currentWorkspace._id}/members/${memberId}/role`,
        { role: newRole },
        { withCredentials: true }
      );
      toast({ title: `Member role updated to ${newRole}` });
      fetchWorkspaceMembers();
    } catch (error) {
      console.error('Error changing member role:', error);
      toast({ 
        title: 'Error changing role', 
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive' 
      });
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    try {
      await axios.delete(
        `${API}/workspaces/${currentWorkspace._id}/members/${memberId}`,
        { withCredentials: true }
      );
      
      toast({ title: `${memberName} removed from workspace` });
      fetchWorkspaces();
    } catch (error) {
      console.error('Error removing member:', error);
      toast({ 
        title: 'Failed to remove member',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive' 
      });
    }
  };

  // Open board type selector first
  const handleOpenBoardCreation = () => {
    setShowBoardTypeSelector(true);
  };

  // Called after board type is selected
  const handleBoardTypeSelected = (boardType) => {
    setSelectedBoardType(boardType);
    setShowBoardTypeSelector(false);
    setShowCreateBoard(true);
  };

  const handleCreateBoard = async () => {
    if (!newBoardName.trim()) {
      toast({ title: 'Board name required', variant: 'destructive' });
      return;
    }

    if (!currentWorkspace) {
      toast({ title: 'Please select a workspace first', variant: 'destructive' });
      return;
    }

    try {
      const response = await axios.post(
        `${API}/boards?workspace_id=${currentWorkspace._id}`,
        {
          name: newBoardName,
          icon: newBoardIcon,
          color: newBoardColor,
          boardType: selectedBoardType
        },
        { withCredentials: true }
      );
      
      const boardTypeNames = {
        project: 'Project board',
        marketing: 'Marketing board',
        sales: 'Sales board'
      };
      
      toast({ 
        title: `${boardTypeNames[selectedBoardType]} created!`,
        description: `"${newBoardName}" is ready to use`
      });
      
      setNewBoardName('');
      setNewBoardIcon('briefcase');
      setNewBoardColor('#FE8309');
      setSelectedBoardType('project');
      setShowCreateBoard(false);
      
      // Refresh boards
      const boardsRes = await axios.get(`${API}/boards?workspace_id=${currentWorkspace._id}`, { withCredentials: true });
      setBoards(boardsRes.data);
      
      // Navigate to the new board
      navigate(`/board?workspace=${currentWorkspace._id}&board=${response.data._id}`);
    } catch (error) {
      console.error('Error creating board:', error);
      toast({ 
        title: 'Error creating board', 
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive' 
      });
    }
  };

  const handleLeaveWorkspace = async () => {
    if (!confirm(`Are you sure you want to leave ${currentWorkspace?.name}?`)) return;
    
    try {
      await axios.post(
        `${API}/workspaces/${currentWorkspace._id}/leave`,
        {},
        { withCredentials: true }
      );
      toast({ title: `You have left ${currentWorkspace.name}` });
      setShowMembersPanel(false);
      
      // Refresh workspaces
      const wsResponse = await axios.get(`${API}/workspaces`, { withCredentials: true });
      setWorkspaces(wsResponse.data);
      
      // If no workspaces left, redirect to onboarding
      if (wsResponse.data.length === 0) {
        navigate('/onboarding');
      } else {
        // Switch to another workspace
        setCurrentWorkspace(wsResponse.data[0]);
        setUserRole(getUserRole(wsResponse.data[0]));
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Error leaving workspace:', error);
      toast({ 
        title: 'Error leaving workspace', 
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive' 
      });
    }
  };

  const handleDeleteWorkspace = async () => {
    try {
      await axios.delete(
        `${API}/workspaces/${currentWorkspace._id}`,
        { withCredentials: true }
      );
      toast({ title: `Workspace "${currentWorkspace.name}" deleted successfully` });
      setShowDeleteWorkspaceDialog(false);
      
      // Refresh workspaces
      const wsResponse = await axios.get(`${API}/workspaces`, { withCredentials: true });
      setWorkspaces(wsResponse.data);
      
      // If no workspaces left, redirect to onboarding
      if (wsResponse.data.length === 0) {
        navigate('/onboarding');
      } else {
        // Switch to another workspace
        setCurrentWorkspace(wsResponse.data[0]);
        setUserRole(getUserRole(wsResponse.data[0]));
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Error deleting workspace:', error);
      toast({ 
        title: 'Error deleting workspace', 
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive' 
      });
    }
  };

  const handleTransferOwnership = async () => {
    if (!selectedNewOwner) {
      toast({ title: 'Please select a new owner', variant: 'destructive' });
      return;
    }
    
    const newOwner = workspaceMembers.find(m => m._id === selectedNewOwner);
    if (!confirm(`Transfer ownership to ${newOwner?.name}? You will become an admin.`)) return;
    
    try {
      await axios.post(
        `${API}/workspaces/${currentWorkspace._id}/transfer-ownership`,
        { new_owner_id: selectedNewOwner },
        { withCredentials: true }
      );
      toast({ title: `Ownership transferred to ${newOwner.name}` });
      setShowTransferOwnershipDialog(false);
      setSelectedNewOwner('');
      
      // Refresh data
      fetchWorkspaceMembers();
      const wsResponse = await axios.get(`${API}/workspaces`, { withCredentials: true });
      setWorkspaces(wsResponse.data);
      const updatedWorkspace = wsResponse.data.find(w => w._id === currentWorkspace._id);
      if (updatedWorkspace) {
        setCurrentWorkspace(updatedWorkspace);
        setUserRole(getUserRole(updatedWorkspace));
      }
    } catch (error) {
      console.error('Error transferring ownership:', error);
      toast({ 
        title: 'Error transferring ownership', 
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive' 
      });
    }
  };


  const fetchBoards = async (workspaceId) => {
    try {
      const boardsRes = await axios.get(`${API}/boards`, { withCredentials: true });
      const workspaceBoards = boardsRes.data.filter(b => b.workspace_id === workspaceId);
      setBoards(workspaceBoards);
      
      // Preload tasks for all boards in background (for instant navigation)
      workspaceBoards.forEach((board, index) => {
        setTimeout(() => {
          axios.get(`${API}/tasks?boardId=${board._id}&workspace_id=${workspaceId}`, { withCredentials: true })
            .catch(err => console.error('Preload error:', err));
        }, index * 200); // Stagger by 200ms
      });
    } catch (error) {
      console.error('Error fetching boards:', error);
    }
  };


  const fetchTodayData = async (workspaceId) => {
    try {
      setTodayLoading(true);
      const res = await axios.get(`${API}/today?workspace_id=${workspaceId}`, { withCredentials: true });
      setTodayData(res.data);
    } catch (error) {
      console.error('Error fetching today data:', error);
    } finally {
      setTodayLoading(false);
    }
  };

  const handleWorkspaceChange = (workspace) => {
    setCurrentWorkspace(workspace);
    setUserRole(getUserRole(workspace));
    // Update URL with new workspace
    navigate(`/dashboard?workspace=${workspace._id}`, { replace: true });
    // Refresh boards for new workspace
    fetchBoards(workspace._id);
    fetchTodayData(workspace._id);
  };

  useEffect(() => {
    // Fetch dashboard data and invitations in parallel
    Promise.all([
      fetchDashboardData(),
      fetchPendingInvitations()
    ]);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch workspaces first (required to know which workspace to load)
      const workspacesRes = await axios.get(`${API}/workspaces`, { withCredentials: true });
      setWorkspaces(workspacesRes.data);
      
      // If no workspaces, redirect to onboarding
      if (workspacesRes.data.length === 0) {
        setLoading(false);
        navigate('/onboarding');
        return;
      }
      
      if (workspacesRes.data.length > 0) {
        // Select workspace from URL or default to first
        const targetWorkspace = urlWorkspaceId 
          ? workspacesRes.data.find(w => w._id === urlWorkspaceId)
          : workspacesRes.data[0];
        
        const selectedWorkspace = targetWorkspace || workspacesRes.data[0];
        setCurrentWorkspace(selectedWorkspace);
        setUserRole(getUserRole(selectedWorkspace));
        fetchTodayData(selectedWorkspace._id).catch(() => {});

        // Fetch all remaining data in PARALLEL for maximum speed
        const [boardsRes, tasksRes] = await Promise.all([
          axios.get(`${API}/boards`, { withCredentials: true }),
          axios.get(`${API}/tasks?workspace_id=${selectedWorkspace._id}`, { withCredentials: true }),
          // Fetch subscription status in parallel but don't wait for it
          fetchSubscriptionStatus(selectedWorkspace._id).catch(err => 
            console.warn('Subscription fetch failed:', err)
          )
        ]);
        
        const tasks = tasksRes.data;
        
        // Calculate task statistics
        const tasksByStatus = {};
        tasks.forEach(task => {
          const status = task.status.label;
          tasksByStatus[status] = (tasksByStatus[status] || 0) + 1;
        });
        
        // Sort tasks by updatedAt and get recent 5
        const sortedTasks = tasks.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        setRecentTasks(sortedTasks.slice(0, 5));
        
        // Set boards for sidebar
        const workspaceBoards = boardsRes.data.filter(b => b.workspace_id === selectedWorkspace._id);
        setBoards(workspaceBoards);
        
        setStats({
          workspaces: workspacesRes.data.length,
          boards: boardsRes.data.length,
          totalTasks: tasks.length,
          tasksByStatus
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast({ title: 'Error loading dashboard', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleViewBoards = () => {
    if (boards.length > 0 && currentWorkspace) {
      navigate(`/board?workspace=${currentWorkspace._id}&board=${boards[0]._id}`);
    } else {
      setShowCreateBoard(true);
    }
  };


  const handleCreateTask = () => {
    if (boards.length > 0 && currentWorkspace) {
      // Navigate to first board where user can create tasks
      navigate(`/board?workspace=${currentWorkspace._id}&board=${boards[0]._id}`);
    } else {
      // If no boards, prompt to create board first
      toast({ 
        title: 'Create a board first', 
        description: 'You need at least one board to create tasks',
        variant: 'default' 
      });
      setShowCreateBoard(true);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F7FB] dark:bg-gray-900">
        {/* Show header immediately for perceived speed */}
        <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700">
          <div className="px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <OptimizedImage 
                  src="/doozum-logo.png" 
                  alt="Doozum" 
                  className="h-4 object-contain"
                  priority={true}
                />
              </div>
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>
        </header>
        
        {/* Minimal loading indicator */}
        <div className="p-6">
          <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-4 space-y-2">
                <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F7FB] dark:bg-gray-900">
      {/* Subscription Banners */}
      {isExpired() && (
        <ReadOnlyBanner onUpgrade={() => navigate('/pricing')} />
      )}
      {isTrialActive() && (
        <TrialBanner onUpgrade={() => navigate('/pricing')} />
      )}
      
      {/* Top Header */}
      <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 sticky top-0 z-40">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <OptimizedImage 
                src="/doozum-logo.png" 
                alt="Doozum" 
                className="h-8 object-contain dark:invert dark:brightness-0 dark:contrast-200 cursor-pointer" 
                priority={true}
                onClick={() => navigate('/')}
              />
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
              
              {/* Workspace Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-gray-900 dark:text-gray-100 font-medium hover:bg-gray-100 dark:hover:bg-gray-700">
                    {currentWorkspace?.name || 'Select Workspace'}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {workspaces.map((workspace) => (
                    <DropdownMenuItem 
                      key={workspace._id}
                      onClick={() => handleWorkspaceChange(workspace)}
                      className={currentWorkspace?._id === workspace._id ? 'bg-gray-100' : ''}
                    >
                      <div className="flex items-center justify-between w-full">
                        <span>{workspace.name}</span>
                        {workspace.owner_id === user?._id && (
                          <span className="text-xs text-gray-500 dark:text-gray-300">Owner</span>
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowCreateWorkspace(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Workspace
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Members Button (only for owner/admin) */}
              {currentWorkspace && (userRole === 'owner' || userRole === 'admin') && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    fetchWorkspaceMembers();
                    setShowMembersPanel(true);
                  }}
                  className="text-gray-600 dark:text-gray-300"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Members
                </Button>
              )}

              {/* Webhooks Button (only for owner/admin) */}
              {currentWorkspace && (userRole === 'owner' || userRole === 'admin') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowWorkspaceWebhooksPanel(true)}
                  className="text-gray-600 dark:text-gray-300"
                >
                  <Webhook className="h-4 w-4 mr-2" />
                  Webhooks
                </Button>
              )}

              {/* Automations Button */}
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-600 dark:text-gray-300"
              >
                <Zap className="h-4 w-4 mr-2" />
                Automations
              </Button>

              {/* Analytics Button */}
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-gray-600 dark:text-gray-300"
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Analytics
              </Button>

              {/* Export Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-gray-600 dark:text-gray-300"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem>
                    <Download className="h-4 w-4 mr-2" />
                    Export as CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Download className="h-4 w-4 mr-2" />
                    Export as PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Download className="h-4 w-4 mr-2" />
                    Export as JSON
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="flex items-center space-x-3">
              {/* Search Bar */}
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                <Input
                  placeholder="Search everything"
                  className="pl-10 w-64 bg-[#F6F7FB] dark:bg-gray-900 border-none"
                />
              </div>
              
              {/* Notification Bell */}
              <button 
                onClick={() => setShowInvitations(!showInvitations)}
                className="relative p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Bell className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                {pendingInvitations.length > 0 && (
                  <span className="absolute top-0 right-0 w-5 h-5 bg-[#1C3E53] dark:bg-[#4A90A4] text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {pendingInvitations.length > 9 ? '9+' : pendingInvitations.length}
                  </span>
                )}
              </button>
              
              {/* Theme Toggle */}
              <ThemeToggle />
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-8 h-8 rounded-full bg-[#1C3E53] dark:bg-[#4A90A4] flex items-center justify-center text-white text-sm font-medium hover:bg-[#2A5470] dark:hover:bg-[#5AA0B4] transition-colors">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                      {userRole && (
                        <span className="text-xs text-gray-400 capitalize">Role: {userRole}</span>
                      )}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                    <SettingsIcon className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  {user?.is_super_admin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/super-admin')} className="cursor-pointer text-purple-600 font-semibold">
                        <Shield className="mr-2 h-4 w-4" />
                        Super Admin Panel
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Dashboard Layout with Sidebar */}
      <div className="flex">
        {/* Left Sidebar */}
        <aside className="w-64 bg-white dark:bg-gray-800 border-r dark:border-gray-700 min-h-[calc(100vh-57px)] p-4">
          <div className="space-y-1">
            {/* Dashboard Tab - Active */}
            <button
              className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-medium"
            >
              <Home className="h-5 w-5" />
              <span className="text-sm">Dashboard</span>
            </button>
            
            {/* Team Chat Button */}
            <button
              onClick={() => navigate(`/board?workspace=${currentWorkspace._id}&view=chat`)}
              className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-all"
            >
              <MessageCircle className="h-5 w-5" />
              <span className="text-sm font-medium">Team Chat</span>
            </button>
            
            {/* GitHub Integration Button */}
            <button
              onClick={() => setShowGitHubPanel(true)}
              className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-all mb-4"
            >
              <Github className="h-5 w-5" />
              <span className="text-sm font-medium">GitHub</span>
            </button>
            
            {/* My Boards Section */}
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase px-3 py-2">My Boards</div>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400 dark:text-gray-500" />
              </div>
            ) : boards.length === 0 ? (
              <div className="px-3">
                <button
                  onClick={handleOpenBoardCreation}
                  className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-[#1C3E53] dark:hover:border-blue-500 transition-all"
                >
                  <Plus className="h-5 w-5" />
                  <span className="text-sm font-medium">Create your first board</span>
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                {boards.map((board) => {
                const boardTypeConfig = {
                  project: { label: 'Project', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300', icon: '🧱' },
                  marketing: { label: 'Marketing', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300', icon: '📈' },
                  sales: { label: 'Sales', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300', icon: '💼' }
                };
                const typeInfo = boardTypeConfig[board.boardType || 'project'];
                
                return (
                  <div key={board._id} className="group relative">
                    <button
                      onClick={() => navigate(`/board?workspace=${currentWorkspace._id}&board=${board._id}`)}
                      onMouseEnter={() => prefetchBoard(board._id, currentWorkspace._id)}
                      className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-all card-hover"
                    >
                      <BoardIcon icon={board.icon} size={20} />
                      <div className="flex-1 flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium truncate">{board.name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${typeInfo.color} whitespace-nowrap`}>
                          {typeInfo.icon} {typeInfo.label}
                        </span>
                      </div>
                    </button>
                  </div>
                );
              })}
              
              {/* Add Board Button (always visible when there are boards) */}
              <div className="px-3 mt-2">
                <button
                  onClick={handleOpenBoardCreation}
                  className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-[#1C3E53] dark:hover:border-blue-500 transition-all"
                >
                  <Plus className="h-5 w-5" />
                  <span className="text-sm font-medium">Add board</span>
                </button>
              </div>
              </div>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
            Bonjour, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 capitalize">
            {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {/* Overdue */}
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700/60 p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-400 to-red-500 rounded-t-2xl" />
            <div className="flex items-start justify-between mb-5">
              <div className="w-11 h-11 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center justify-center ring-1 ring-red-100 dark:ring-red-800/30">
                <AlertTriangle className="h-5 w-5 text-red-500" />
              </div>
              {todayLoading && <Loader2 className="h-4 w-4 animate-spin text-gray-300 mt-1" />}
            </div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">En retard</p>
            <h3 className="text-4xl font-bold text-gray-900 dark:text-gray-100 tabular-nums tracking-tight">{todayData.kpis.overdue_count}</h3>
          </div>

          {/* Today */}
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700/60 p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-orange-400 to-amber-400 rounded-t-2xl" />
            <div className="flex items-start justify-between mb-5">
              <div className="w-11 h-11 bg-orange-50 dark:bg-orange-900/20 rounded-xl flex items-center justify-center ring-1 ring-orange-100 dark:ring-orange-800/30">
                <CalendarDays className="h-5 w-5 text-[#FE8309]" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Aujourd'hui</p>
            <h3 className="text-4xl font-bold text-gray-900 dark:text-gray-100 tabular-nums tracking-tight">{todayData.kpis.today_count}</h3>
          </div>

          {/* Pipeline */}
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700/60 p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-400 to-green-500 rounded-t-2xl" />
            <div className="flex items-start justify-between mb-5">
              <div className="w-11 h-11 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl flex items-center justify-center ring-1 ring-emerald-100 dark:ring-emerald-800/30">
                <DollarSign className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Pipeline actif</p>
            <h3 className="text-4xl font-bold text-gray-900 dark:text-gray-100 tabular-nums tracking-tight">
              {todayData.kpis.pipeline_value > 0
                ? `€${todayData.kpis.pipeline_value >= 1000
                    ? (todayData.kpis.pipeline_value / 1000).toFixed(1) + 'k'
                    : todayData.kpis.pipeline_value.toLocaleString('fr-FR')}`
                : '€0'}
            </h3>
          </div>
        </div>

        {/* Invitations Section - Only show if there are pending invitations */}
        {pendingInvitations.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Workspace Invitations</h2>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">{pendingInvitations.length} pending</span>
            </div>
            
            <div className="space-y-4">
              {pendingInvitations.map((invitation) => (
                <div key={invitation._id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">{invitation.workspace_name}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      <span className="font-medium">{invitation.invited_by}</span> invited you as <span className="font-medium text-green-600">{invitation.role}</span>
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Expires: {new Date(invitation.expiresAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      onClick={() => handleAcceptInvitation(invitation._id)}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Accept
                    </Button>
                    <Button
                      onClick={() => handleDeclineInvitation(invitation._id)}
                      size="sm"
                      variant="outline"
                      className="text-gray-600 hover:text-gray-900"
                    >
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main Dashboard Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Today Feed - 2/3 */}
          <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700/60 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 tracking-tight">Vue Aujourd'hui</h2>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 capitalize">
                  {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
              </div>
            </div>
            <TodayFeed
              overdue={todayData.overdue}
              today={todayData.today}
              thisWeek={todayData.this_week}
              loading={todayLoading}
              onRefresh={() => currentWorkspace && fetchTodayData(currentWorkspace._id)}
            />
          </div>

          {/* Right Column - 1/3 */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700/60 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <Button
                  onClick={handleOpenBoardCreation}
                  disabled={!canEdit()}
                  className="w-full bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white justify-start disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!canEdit() ? "Mode lecture seule - Passez à la version payante" : ""}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Board
                </Button>
                <Button onClick={handleViewBoards} variant="outline" className="w-full justify-start">
                  <LayoutGrid className="h-4 w-4 mr-2" />
                  View All Boards
                </Button>
                <Button onClick={handleCreateTask} variant="outline" className="w-full justify-start">
                  <ListTodo className="h-4 w-4 mr-2" />
                  Create Task
                </Button>
                <Button
                  onClick={() => navigate(`/chat?workspace=${currentWorkspace._id}`)}
                  variant="outline"
                  className="w-full justify-start border-[#FE8309] text-[#FE8309] hover:bg-orange-50 dark:hover:bg-orange-900/10"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Open Team Chat
                </Button>
              </div>
            </div>

            {/* Activity Feed */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700/60 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Activité récente</h2>
                <Clock className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              </div>
              {recentTasks.length === 0 ? (
                <div className="text-center py-6">
                  <ListTodo className="h-10 w-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Aucune activité récente</p>
                  <Button onClick={handleOpenBoardCreation} size="sm" className="mt-3 bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white">
                    <Plus className="h-3 w-3 mr-1" />
                    Créer un board
                  </Button>
                </div>
              ) : (
                <div className="space-y-1">
                  {recentTasks.slice(0, 5).map((task) => (
                    <div
                      key={task._id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer min-h-[44px]"
                      onClick={handleViewBoards}
                    >
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                        style={{ backgroundColor: task.person?.color || '#1C3E53' }}
                      >
                        {task.person?.avatar || task.person?.name?.charAt(0) || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{task.task}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(task.updatedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        </p>
                      </div>
                      <span
                        className="flex-shrink-0 text-xs px-2 py-0.5 rounded font-medium text-white"
                        style={{ backgroundColor: task.status?.color || '#CCCCCC' }}
                      >
                        {task.status?.label || 'Unknown'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      </div>


      {/* Create Workspace Dialog */}
      <Dialog open={showCreateWorkspace} onOpenChange={setShowCreateWorkspace}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Workspace</DialogTitle>
            <DialogDescription>
              Create a workspace to organize your boards and collaborate with team members
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="workspace-name">Workspace Name</Label>
              <Input
                id="workspace-name"
                placeholder="e.g., My Team Workspace"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateWorkspace()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateWorkspace(false)}>Cancel</Button>
            <Button onClick={handleCreateWorkspace} className="bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white">Create Workspace</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Member Dialog */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Member to Workspace</DialogTitle>
            <DialogDescription>
              Invite team members by email. They must have an account to join.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email Address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="member@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleInviteMember()}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member - Can view and update assigned tasks</SelectItem>
                  <SelectItem value="admin">Admin - Can create boards and tasks, invite members</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteModal(false)}>Cancel</Button>
            <Button onClick={handleInviteMember} className="bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white">Send Invitation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workspace Members Panel */}
      <Sheet open={showMembersPanel} onOpenChange={(open) => {
        setShowMembersPanel(open);
        if (open) {
          fetchWorkspaceMembers();
        }
      }}>
        <SheetContent className="w-[400px] sm:w-[540px]">
          <SheetHeader>
            <SheetTitle>Workspace Members</SheetTitle>
            <SheetDescription>
              Manage members and their roles in {currentWorkspace?.name}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            {/* Invite Button */}
            {(userRole === 'owner' || userRole === 'admin') && (
              <Button 
                onClick={() => {
                  setShowMembersPanel(false);
                  setShowInviteModal(true);
                }}
                className="w-full bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Invite Member
              </Button>
            )}

            {/* Members List */}
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Active Members</h3>
              {workspaceMembers.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No members yet</p>
                </div>
              ) : (
                workspaceMembers.map((member) => {
                  const isCurrentUser = user?._id === member._id;
                  const canManageMember = 
                    (userRole === 'owner') || 
                    (userRole === 'admin' && member.role === 'member');
                  const canChangeRole = 
                    (userRole === 'owner' && member.role !== 'owner') ||
                    (userRole === 'admin' && member.role === 'member');
                  
                  return (
                    <div 
                      key={member._id} 
                      className="flex items-center justify-between p-4 border dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium"
                          style={{backgroundColor: '#1C3E53'}}
                        >
                          {member.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {member.name} {isCurrentUser && <span className="text-xs text-gray-500 dark:text-gray-400">(You)</span>}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{member.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {/* Role Badge/Selector */}
                        {canChangeRole && !isCurrentUser ? (
                          <Select 
                            value={member.role}
                            onValueChange={(newRole) => handleChangeMemberRole(member._id, newRole)}
                          >
                            <SelectTrigger className="w-28 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="member">Member</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                            member.role === 'owner' 
                              ? 'bg-purple-100 text-purple-700'
                              : member.role === 'admin' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                          </span>
                        )}
                        
                        {/* Remove Member Button */}
                        {canManageMember && !isCurrentUser && member.role !== 'owner' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveMember(member._id, member.name)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Sent Invitations Section */}
            {(userRole === 'owner' || userRole === 'admin') && (
              <div className="mt-8 space-y-3 pt-6 border-t dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Sent Invitations</h3>
                  {loadingSentInvitations && (
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  )}
                </div>
                {sentInvitations.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-4">No pending invitations</p>
                ) : (
                  sentInvitations.map((invitation) => (
                    <div 
                      key={invitation._id}
                      className="p-4 border dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 dark:text-gray-100">{invitation.email}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                              invitation.status === 'pending' && !invitation.is_expired
                                ? 'bg-yellow-100 text-yellow-700'
                                : invitation.status === 'accepted'
                                ? 'bg-green-100 text-green-700'
                                : invitation.status === 'rejected'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {invitation.is_expired ? 'Expired' : invitation.status.charAt(0).toUpperCase() + invitation.status.slice(1)}
                            </span>
                            <span className="text-xs text-gray-500">
                              as {invitation.role}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Sent: {new Date(invitation.createdAt).toLocaleDateString()}
                          </p>
                          {invitation.status === 'pending' && !invitation.is_expired && (
                            <p className="text-xs text-gray-500">
                              Expires: {new Date(invitation.expiresAt).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 ml-2">
                          {(invitation.status === 'rejected' || invitation.is_expired) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleResendInvitation(invitation._id)}
                            >
                              Resend
                            </Button>
                          )}
                          {invitation.status === 'pending' && !invitation.is_expired && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteInvitation(invitation._id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              Cancel
                            </Button>
                          )}
                          {(invitation.status === 'accepted' || invitation.status === 'rejected') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteInvitation(invitation._id)}
                              className="text-gray-400 hover:text-red-600"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            
            {/* Workspace Management Actions */}
            <div className="mt-8 space-y-3 pt-6 border-t dark:border-gray-600">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Workspace Management</h3>
              
              {/* Transfer Ownership (Owner only) */}
              {userRole === 'owner' && (
                <Button
                  variant="outline"
                  className="w-full justify-start text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={() => {
                    setShowMembersPanel(false);
                    setShowTransferOwnershipDialog(true);
                  }}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Transfer Ownership
                </Button>
              )}
              
              {/* Leave Workspace (Non-owners) */}
              {userRole !== 'owner' && (
                <Button
                  variant="outline"
                  className="w-full justify-start text-orange-600 border-orange-200 hover:bg-orange-50"
                  onClick={handleLeaveWorkspace}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Leave Workspace
                </Button>
              )}
              
              {/* Delete Workspace (Owner only) */}
              {userRole === 'owner' && (
                <Button
                  variant="outline"
                  className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => {
                    setShowMembersPanel(false);
                    setShowDeleteWorkspaceDialog(true);
                  }}
                >
                  <X className="h-4 w-4 mr-2" />
                  Delete Workspace
                </Button>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>


      {/* Workspace Webhooks Panel */}
      <Sheet open={showWorkspaceWebhooksPanel} onOpenChange={setShowWorkspaceWebhooksPanel}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>Webhooks du workspace</SheetTitle>
            <SheetDescription>
              Ces webhooks s'appliquent à tous les boards du workspace.
            </SheetDescription>
          </SheetHeader>
          {currentWorkspace && (
            <WebhooksSettings
              workspaceId={currentWorkspace._id}
              boardId={null}
              level="workspace"
              userRole={userRole}
            />
          )}
        </SheetContent>
      </Sheet>


      {/* Delete Workspace Dialog */}
      <Dialog open={showDeleteWorkspaceDialog} onOpenChange={setShowDeleteWorkspaceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Workspace</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{currentWorkspace?.name}</strong>? 
              This will permanently delete all boards and tasks. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteWorkspaceDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteWorkspace}
            >
              Delete Workspace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Board Type Selector */}
      <BoardTypeSelector
        open={showBoardTypeSelector}
        onClose={() => setShowBoardTypeSelector(false)}
        onSelect={handleBoardTypeSelected}
      />

      {/* Create Board Dialog */}
      <Dialog open={showCreateBoard} onOpenChange={setShowCreateBoard}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Board</DialogTitle>
            <DialogDescription>
              Add a new board to organize your tasks and projects
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="boardName">Board Name</Label>
              <Input
                id="boardName"
                value={newBoardName}
                onChange={(e) => setNewBoardName(e.target.value)}
                placeholder="e.g., Marketing Campaign, Product Development"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateBoard();
                  }
                }}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Icon</Label>
                <Select value={newBoardIcon} onValueChange={setNewBoardIcon}>
                  <SelectTrigger>
                    <div className="flex items-center gap-2">
                      <BoardIcon icon={newBoardIcon} size={18} />
                      <span>{BOARD_ICONS[newBoardIcon]?.label || 'Select Icon'}</span>
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(BOARD_ICONS).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <BoardIcon icon={key} size={18} />
                          <span>{label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Color</Label>
                <Select value={newBoardColor} onValueChange={setNewBoardColor}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="#FE8309">🟠 Orange</SelectItem>
                    <SelectItem value="#4A90A4">🟣 Purple</SelectItem>
                    <SelectItem value="#00C875">🟢 Green</SelectItem>
                    <SelectItem value="#0073EA">🔵 Blue</SelectItem>
                    <SelectItem value="#FDAB3D">🟡 Yellow</SelectItem>
                    <SelectItem value="#E44258">🔴 Red</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateBoard(false);
              setNewBoardName('');
              setNewBoardIcon('briefcase');
              setNewBoardColor('#FE8309');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateBoard}
              className="bg-[#FE8309] hover:bg-[#E57408]"
              disabled={!newBoardName.trim()}
            >
              Create Board
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Ownership Dialog */}
      <Dialog open={showTransferOwnershipDialog} onOpenChange={setShowTransferOwnershipDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer Ownership</DialogTitle>
            <DialogDescription>
              Select a member to become the new owner of <strong>{currentWorkspace?.name}</strong>. 
              You will become an admin after the transfer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Label htmlFor="newOwner">New Owner</Label>
            <Select value={selectedNewOwner} onValueChange={setSelectedNewOwner}>
              <SelectTrigger>
                <SelectValue placeholder="Select a member" />
              </SelectTrigger>
              <SelectContent>
                {workspaceMembers
                  .filter(m => m._id !== user?._id && m.role !== 'owner')
                  .map(member => (
                    <SelectItem key={member._id} value={member._id}>
                      {member.name} ({member.email}) - {member.role}
                    </SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowTransferOwnershipDialog(false);
              setSelectedNewOwner('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleTransferOwnership}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Transfer Ownership
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* GitHub Integration Dialog */}
      <Dialog open={showGitHubPanel} onOpenChange={setShowGitHubPanel}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Github className="h-5 w-5" />
              GitHub Integration
            </DialogTitle>
            <DialogDescription>
              Connect your GitHub repositories to sync commits, issues, and PRs
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {currentWorkspace && (
              <GitHubIntegration workspaceId={currentWorkspace._id} />
            )}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Dashboard;
